import { useAtomValue } from 'jotai';
import { ReactNode, useMemo, useState } from 'react';
import {
  Dropdown,
  InlineLoading,
  Modal,
  ModalBody,
  Tag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { PlatSvg } from 'components/polygon-svg';
import { format2 } from 'utils/number';
import {
  formatAllocationMethod,
  formatUnitAgreementType,
  NULL_DISPLAY_VALUE,
} from '../utils';
import { useWellAllocationManagersAtom } from './atoms';
import {
  AllocationMethod,
  ComparisonColumn,
  useComparisonColumn,
} from './hooks';
import { ComparisonAgreement, WellAllocationManagersResponse } from './types';
import styles from './comparison-modal.module.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The well whose agreements are compared. */
  wellUWI: string | null;
  /** The unit the user is currently viewing — pinned as the left column. */
  currentUnitName: string;
}

/**
 * Formats a measurement with its unit, or the placeholder when absent.
 *
 * @param {number | null} value - Measurement to render.
 * @param {string} unitLabel - 'ac' or 'ft'.
 * @return {string} e.g. "640.00 ac", or '--'.
 */
const formatMeasure = (value: number | null, unitLabel: string): string => {
  if (value === null || value === undefined) return NULL_DISPLAY_VALUE;
  return `${format2(value)} ${unitLabel}`;
};

/**
 * Formats an allocation factor (a 0–1 ratio) as a percentage.
 *
 * @param {number | null} factor - Ratio to render.
 * @return {string} e.g. "42.50%", or '--'.
 */
const formatFactor = (factor: number | null): string => {
  if (factor === null || factor === undefined) return NULL_DISPLAY_VALUE;
  return `${format2(factor * 100)}%`;
};

/**
 * Renders an Allocation Manager version for display.
 *
 * The version number itself is derived server-side (development area temporal
 * versions for the major, the manager's own for the minor) — only the framing is
 * added here.
 *
 * "(Current)" is unconditional because the modal always reads the live manager, so
 * the version it shows is always the current one. If the modal is ever made
 * version-aware this has to become conditional, the way the unit page's version
 * switcher does it, or it will assert something false.
 *
 * @param {string | null | undefined} version - Version string such as "1.1".
 * @return {string} e.g. "Version 1.1 (Current)", or the null placeholder.
 */
const formatVersion = (version: string | null | undefined): string => {
  if (!version) return NULL_DISPLAY_VALUE;
  return `Version ${version} (Current)`;
};

/**
 * The active allocation method, labelled as the mock does with an "Allocation" suffix.
 *
 * Deliberately separate from `formatAllocationMethod`: that one also feeds the method
 * dropdown and the Methods Available row, where the bare "Acreage" / "Lateral Length"
 * is what belongs.
 *
 * @param {string | null | undefined} method - Stored enum value.
 * @return {string} e.g. "Lateral Length Allocation", or the null placeholder.
 */
const formatActiveAllocationMethod = (
  method: string | null | undefined
): string => {
  const label = formatAllocationMethod(method);
  return label === NULL_DISPLAY_VALUE ? label : `${label} Allocation`;
};

/**
 * The GIS validation verdict for one column.
 *
 * Reuses the unit page's warning set rather than re-deriving thresholds: any warning
 * at all is a breach.
 *
 * @param {ComparisonColumn} column - Derived column to judge.
 * @return {JSX.Element} A red "Threshold breached" or green "Within tolerance" tag.
 */
const GisValidationTag = ({ column }: { column: ComparisonColumn }) => {
  if (!column.agreement?.allocationManager) {
    return <span>{NULL_DISPLAY_VALUE}</span>;
  }
  const breached = column.warnings.length > 0;
  return (
    <Tag type={breached ? 'red' : 'green'} size="md">
      {breached
        ? `Threshold breached (${column.warnings.length})`
        : 'Within tolerance'}
    </Tag>
  );
};

/**
 * The Tract Allocation sub-table for one column: a row per tract under the selected
 * method, then the Total Value and Sourced-vs-GIS footers.
 *
 * @param {object} props - Wrapper for the single prop.
 * @param {ComparisonColumn} props.column - Derived column to tabulate.
 * @return {JSX.Element} The table, or the null placeholder when there are no tracts.
 */
const TractAllocationTable = ({ column }: { column: ComparisonColumn }) => {
  if (column.rows.length === 0) {
    return <span>{NULL_DISPLAY_VALUE}</span>;
  }

  return (
    <Table size="xs" className={styles.tractTable}>
      <TableHead>
        <TableRow>
          <TableHeader>Tract</TableHeader>
          <TableHeader>Value ({column.unitLabel})</TableHeader>
          <TableHeader>Factor</TableHeader>
          <TableHeader>GIS ({column.unitLabel})</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {column.rows.map((row, idx) => (
          <TableRow key={`${row.tractName ?? 'tract'}-${idx}`}>
            <TableCell>{row.tractName ?? NULL_DISPLAY_VALUE}</TableCell>
            <TableCell>
              {row.value === null ? NULL_DISPLAY_VALUE : format2(row.value)}
            </TableCell>
            <TableCell>{formatFactor(row.factor)}</TableCell>
            <TableCell>
              {row.gisValue === null
                ? NULL_DISPLAY_VALUE
                : format2(row.gisValue)}
            </TableCell>
          </TableRow>
        ))}
        <TableRow className={styles.totalRow}>
          <TableCell>Total Value (Sum)</TableCell>
          <TableCell>
            {column.totalValueSum === null
              ? NULL_DISPLAY_VALUE
              : format2(column.totalValueSum)}
          </TableCell>
          <TableCell>{formatFactor(column.totalFactor)}</TableCell>
          <TableCell>
            {column.totalGisValueSum === null
              ? NULL_DISPLAY_VALUE
              : format2(column.totalGisValueSum)}
          </TableCell>
        </TableRow>
        {/*
          The row's two operands, each under the column that names it: the plat
          figure under Value, the unit's GIS figure under GIS. The variance itself
          is reported once, in the side-by-side summary below.
        */}
        <TableRow className={styles.totalRow}>
          <TableCell>Sourced vs GIS</TableCell>
          <TableCell>
            {column.platValue === null
              ? NULL_DISPLAY_VALUE
              : format2(column.platValue)}
          </TableCell>
          <TableCell />
          <TableCell>
            {column.sourcedValue === null
              ? NULL_DISPLAY_VALUE
              : format2(column.sourcedValue)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

/**
 * Side-by-side comparison of the Allocation Managers on every unit agreement a well
 * is allocated under.
 *
 * The left column is pinned to the unit the user came from. The right column is
 * chosen from the well's other agreements, with its own allocation-method selector so
 * a JOA's acreage split can be read against a D&S unit's lateral split.
 *
 * @param {Props} props - Open state, close handler, well UWI and the pinned unit name.
 * @return {JSX.Element | null} The modal, or null while closed.
 */
export const ComparisonModal = ({
  isOpen,
  onClose,
  wellUWI,
  currentUnitName,
}: Props) => {
  const dataAtom = useWellAllocationManagersAtom(isOpen ? wellUWI : null);
  const { loading, data } = useAtomValue(dataAtom);

  const response =
    data && !('error' in data)
      ? (data as WellAllocationManagersResponse)
      : null;
  const errorMessage = data && 'error' in data ? data.error : null;

  // Memoised so the derivations below don't see a fresh [] identity every render.
  const agreements = useMemo(() => response?.agreements ?? [], [response]);

  const leftAgreement = useMemo<ComparisonAgreement | null>(
    () => agreements.find((a) => a.unitName === currentUnitName) ?? null,
    [agreements, currentUnitName]
  );

  // Every other agreement on the well, before type scoping — kept so the empty state
  // can tell "only one agreement" apart from "none of a different type".
  const otherAgreementsOnWell = useMemo(
    () => agreements.filter((a) => a.unitName !== currentUnitName),
    [agreements, currentUnitName]
  );

  // Scoped to a different agreement type: the comparison exists to read a JOA against
  // the D&S covering the same well, so a same-type pairing is not what it is for.
  //
  // Falls back to every other agreement when the current unit has no type. The column
  // is nullable and GIS has not populated it yet, so scoping on an absent type would
  // empty the dropdown on all existing data.
  const otherAgreements = useMemo(() => {
    const leftType = leftAgreement?.unitAgreementType;
    if (!leftType) return otherAgreementsOnWell;

    return otherAgreementsOnWell.filter(
      (a) => a.unitAgreementType !== leftType
    );
  }, [leftAgreement, otherAgreementsOnWell]);

  // The list is already scoped to the opposite type, so the first entry IS the
  // intended pairing — no per-type special case, and no direction asymmetry.
  const defaultRightUnit = useMemo(
    () => otherAgreements[0]?.unitName ?? null,
    [otherAgreements]
  );

  const [rightUnitOverride, setRightUnitOverride] = useState<string | null>(
    null
  );
  const [rightMethodOverride, setRightMethodOverride] =
    useState<AllocationMethod | null>(null);

  const rightUnitName = rightUnitOverride ?? defaultRightUnit;
  const rightAgreement =
    otherAgreements.find((a) => a.unitName === rightUnitName) ?? null;

  const leftMethod = (leftAgreement?.allocationManager?.allocationMethod ??
    'acreage') as AllocationMethod;
  const rightMethod =
    rightMethodOverride ??
    ((rightAgreement?.allocationManager?.allocationMethod ??
      'acreage') as AllocationMethod);

  const leftColumn = useComparisonColumn(leftAgreement, leftMethod);
  const rightColumn = useComparisonColumn(rightAgreement, rightMethod);

  const rightMethodOptions = rightAgreement?.allocationManager
    ?.methodsAvailable ?? ['acreage'];

  // Dropdown items are unit names (selection is keyed on them), so the type is looked
  // up for display rather than carried in the item itself.
  const unitOptionLabel = (unitName: string | null) => {
    if (!unitName) return '';

    const agreement = otherAgreements.find((a) => a.unitName === unitName);
    const type = agreement?.unitAgreementType;
    return type ? `${unitName} (${formatUnitAgreementType(type)})` : unitName;
  };

  if (!isOpen) return null;

  const wellLabel =
    response?.wellName || response?.wellUWI || wellUWI || 'This well';

  const plat = (column: ComparisonColumn) => (
    <PlatSvg
      tracts={column.rows.map((row) => ({
        geometry: row.geometry,
        label: row.tractName,
      }))}
      unitGeometry={column.agreement?.unitGeometry ?? null}
      wellGeometry={response?.wellGeometry ?? null}
      width={240}
      height={200}
    />
  );

  const rowsToRender: Array<{
    label: string;
    left: ReactNode;
    right: ReactNode;
  }> = [
    { label: 'Plat', left: plat(leftColumn), right: plat(rightColumn) },
    {
      label: 'Tract Allocation',
      left: <TractAllocationTable column={leftColumn} />,
      right: <TractAllocationTable column={rightColumn} />,
    },
    {
      label: 'Unit Agreement Type',
      left: formatUnitAgreementType(leftAgreement?.unitAgreementType),
      right: formatUnitAgreementType(rightAgreement?.unitAgreementType),
    },
    {
      label: 'Allocation Manager Version',
      left: formatVersion(leftAgreement?.allocationManager?.version),
      right: formatVersion(rightAgreement?.allocationManager?.version),
    },
    {
      label: 'Active Allocation Method',
      left: formatActiveAllocationMethod(
        leftAgreement?.allocationManager?.allocationMethod
      ),
      right: formatActiveAllocationMethod(
        rightAgreement?.allocationManager?.allocationMethod
      ),
    },
    {
      label: 'Methods Available',
      left:
        leftAgreement?.allocationManager?.methodsAvailable
          .map(formatAllocationMethod)
          .join(', ') ?? NULL_DISPLAY_VALUE,
      right:
        rightAgreement?.allocationManager?.methodsAvailable
          .map(formatAllocationMethod)
          .join(', ') ?? NULL_DISPLAY_VALUE,
    },
    {
      label: 'Tracts',
      left: leftAgreement?.allocationManager?.tractCount ?? NULL_DISPLAY_VALUE,
      right:
        rightAgreement?.allocationManager?.tractCount ?? NULL_DISPLAY_VALUE,
    },
    {
      label: 'Total Value (Sum)',
      left: formatMeasure(leftColumn.totalValueSum, leftColumn.unitLabel),
      right: formatMeasure(rightColumn.totalValueSum, rightColumn.unitLabel),
    },
    {
      label: 'Total GIS Calc Value (Sum)',
      left: formatMeasure(leftColumn.totalGisValueSum, leftColumn.unitLabel),
      right: formatMeasure(rightColumn.totalGisValueSum, rightColumn.unitLabel),
    },
    {
      label: 'Sourced vs GIS Variance',
      left:
        leftColumn.variancePercent === null
          ? NULL_DISPLAY_VALUE
          : `${format2(leftColumn.variancePercent)}%`,
      right:
        rightColumn.variancePercent === null
          ? NULL_DISPLAY_VALUE
          : `${format2(rightColumn.variancePercent)}%`,
    },
    {
      label: 'GIS Validation',
      left: <GisValidationTag column={leftColumn} />,
      right: <GisValidationTag column={rightColumn} />,
    },
    {
      label: 'Intersecting Well',
      left: wellLabel,
      right: wellLabel,
    },
    {
      label: 'Operator',
      left: response?.operator || NULL_DISPLAY_VALUE,
      right: response?.operator || NULL_DISPLAY_VALUE,
    },
  ];

  return (
    <Modal
      open={isOpen}
      passiveModal
      hasScrollingContent
      size="lg"
      className={styles.comparisonModal}
      onRequestClose={onClose}
      modalHeading="Compare Allocation Managers"
      aria-label="Compare allocation managers"
    >
      {/* The comparison is taller than the viewport on most screens. ModalBody is what
          carries Carbon's scroll region — without it the rows clip instead of scroll. */}
      <ModalBody className={styles.modalBody}>
        {loading && <InlineLoading description="Loading agreements..." />}

        {!loading && errorMessage && (
          <div className={styles.emptyState}>
            Could not load this well&apos;s agreements: {errorMessage}
          </div>
        )}

        {!loading && !errorMessage && (
          <>
            <p className={styles.intro}>
              <strong>{wellLabel}</strong> is allocated under{' '}
              {response?.agreementCount ?? 0} unit agreement
              {(response?.agreementCount ?? 0) === 1 ? '' : 's'}. Choose which
              one to compare this unit against.
            </p>

            {otherAgreements.length === 0 ? (
              <div className={styles.emptyState}>
                {otherAgreementsOnWell.length === 0
                  ? 'This well is allocated under a single unit agreement, so there is nothing to compare it against.'
                  : `Every other agreement on this well is also ${formatUnitAgreementType(
                      leftAgreement?.unitAgreementType
                    )}. There is no agreement of a different type to compare this unit against.`}
              </div>
            ) : (
              <>
                <h4 className={styles.sectionTitle}>Allocation Managers</h4>

                <div className={styles.columnHeaders}>
                  <div className={styles.labelCell}>Attribute</div>
                  <div className={styles.headerCell}>
                    <div className={styles.unitIdentity}>
                      <span className={styles.unitName}>{currentUnitName}</span>
                      <span className={styles.unitType}>
                        {formatUnitAgreementType(
                          leftAgreement?.unitAgreementType
                        )}
                      </span>
                      <Tag type="blue" size="md">
                        This unit
                      </Tag>
                    </div>
                  </div>
                  <div className={styles.headerCell}>
                    <div className={styles.selectors}>
                      <Dropdown
                        id="comparison-unit"
                        titleText=""
                        label="Select a unit..."
                        size="sm"
                        className={styles.unitDropdown}
                        items={otherAgreements.map((a) => a.unitName)}
                        selectedItem={rightUnitName}
                        onChange={({
                          selectedItem,
                        }: {
                          selectedItem: string | null;
                        }) => {
                          setRightUnitOverride(selectedItem);
                          // The new unit has its own active method; drop the override
                          // so it shows that rather than the previous unit's choice.
                          setRightMethodOverride(null);
                        }}
                        itemToString={unitOptionLabel}
                      />
                      <Dropdown
                        id="comparison-method"
                        titleText=""
                        label="Allocation method"
                        size="sm"
                        className={styles.methodDropdown}
                        items={rightMethodOptions}
                        selectedItem={rightMethod}
                        onChange={({
                          selectedItem,
                        }: {
                          selectedItem: string | null;
                        }) =>
                          setRightMethodOverride(
                            (selectedItem as AllocationMethod) ?? null
                          )
                        }
                        itemToString={(item: string | null) =>
                          formatAllocationMethod(item)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.comparisonGrid}>
                  {rowsToRender.map((row) => (
                    <div key={row.label} className={styles.comparisonRow}>
                      <div className={styles.labelCell}>{row.label}</div>
                      <div className={styles.valueCell}>{row.left}</div>
                      <div className={styles.valueCell}>{row.right}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};
